/* eslint-disable multiline-ternary */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable max-statements */
import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { usePostHog } from "posthog-js/react";
import { Grid } from "@mui/material";
// (combined imports above)
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik, FormikProvider, type FormikTouched } from "formik";
import * as yup from "yup";
import {
  StepBreadcrumbs,
  useStepBreadcrumbsStyles,
} from "components/steps/step-breadcrumbs";
import {
  getTeamByOrgId,
  getTeamByOrgIdVariables,
  plansAll,
  plansAll_plansAll_defaultPricing as PlanPricingGql,
  getTeamByOrgId_organization_team as TeamUserGql,
  invoicedLetterCosts,
  planPricingQuote,
  planPricingQuoteVariables,
} from "types/gql-types";
import { usePostgresViewer } from "components/viewer/context";
import { sha256Hex } from "utils/hash";
import { GET_PLANS_ALL, GET_PLAN_PRICING_QUOTE } from "pages/dashboard/admin/plans/queries";
import { TEAM_BY_ORG } from "../pages/team";
import { propertyInfoSchema } from "./validation";
import { NEW_PROPERTY_STEPS } from "./steps";
import { LOAD_PROPERTY_CONTRACT_PROGRESS, SAVE_PROPERTY_CONTRACT_PROGRESS } from "./add-property/mutations";
import StepChoosePlan from "./add-property/step-1-choose-plan";
import StepAddNewProperty from "./add-property/step-2-add-property";
import StepPropertyInfo from "./add-property/step-3-property-info";
import StepSignerInfo from "./add-property/step-4-signer-info";
import StepPayment from "./add-property/step-5-payment";
import StepReview from "./add-property/step-6-review";
import StepLayout from "./add-property/step-layout";
import { GET_INVOICED_LETTER_COSTS } from "./queries";
import StepSuccess, { type ContractSuccessData } from "./add-property/step-7-success";
import type { CommonStepProps } from "./step-props";

type PlanRow = CommonStepProps["plans"][number];

export type NewPropertyFormValues = {
  planId: string;
  propertyName: string;
  unitCount: number | "";
  billingCycle: "annually" | "quarterly";
  propertyInfo: {
    email: string;
    phone: string;
    legalName: string;
    phoneExtension: string;
    inDevelopment: boolean;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  signer: {
    selfSigning: boolean;
    title: string;
    signerUserId: string;
  };
  payment: {
    method: "invoice" | "credit_card" | "ach";
    accountingEmail: string;
  };
};

const NEW_PROPERTY_INITIAL_VALUES: NewPropertyFormValues = {
  planId: "",
  propertyName: "",
  unitCount: "",
  billingCycle: "annually",
  propertyInfo: {
    email: "",
    phone: "",
    legalName: "",
    phoneExtension: "",
    inDevelopment: false,
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
  },
  signer: {
    selfSigning: false,
    title: "",
    signerUserId: "",
  },
  payment: {
    method: "invoice",
    accountingEmail: "",
  },
};

const validationSchema = yup.object({
  planId: yup
    .string()
    .trim()
    .min(1, "Please select a plan"),
  billingCycle: yup
    .string()
    .oneOf(["annually", "quarterly"])
    .required(),
  propertyName: yup.string().required("Property name is required"),
  unitCount: yup
    .number()
    .required("Unit count is required")
    .typeError("Unit count is required")
    .integer("Unit count must be a whole number")
    .min(1, "1 unit minimum")
    .max(20000, "Unit count max is 20,000"),
  payment: yup.object({
    method: yup.string().oneOf(["invoice", "credit_card", "ach"]).required(),
    accountingEmail: yup
      .string()
      .validEmail("Invalid email")
      .when("method", ([method], schema) =>
        (method === "invoice"
          ? schema.required("Accounting email is required")
          : schema.notRequired())),
  }),
  propertyInfo: propertyInfoSchema,
  signer: yup.object({
    selfSigning: yup.boolean().required(),
    title: yup.string().required("Signer title is required"),
    signerUserId: yup.string(),
  }),
});

const REVIEW_STEP_INDEX = 5;
const SUCCESS_STEP_INDEX = 6;

const NEW_PROPERTY_PATH_SUFFIX_TO_STEP: Record<string, number> = {
  "/choose": 0,
  "/add": 1,
  "/info": 2,
  "/signer": 3,
  "/payment": 4,
  "/review": 5,
  "/success": 6,
};

const PROPERTY_NEW_CONTRACT_SUCCESS_KEY = "propertyNewContractSuccessV1";

const getNewPropertyStepFromLocation = function (): number {
  if (typeof window === "undefined") return 0;
  const sub = window.location.pathname.split("/property/new")[1] || "/choose";
  return NEW_PROPERTY_PATH_SUFFIX_TO_STEP[sub] ?? 0;
};

const readStoredContractSuccess = function (): ContractSuccessData | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PROPERTY_NEW_CONTRACT_SUCCESS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ContractSuccessData;
  } catch {
    return null;
  }
};

const writeStoredContractSuccess = function (data: ContractSuccessData): void {
  try {
    sessionStorage.setItem(
      PROPERTY_NEW_CONTRACT_SUCCESS_KEY,
      JSON.stringify(data),
    );
  } catch {
    // best-effort
  }
};

const clearStoredContractSuccess = function (): void {
  try {
    sessionStorage.removeItem(PROPERTY_NEW_CONTRACT_SUCCESS_KEY);
  } catch {
    // best-effort
  }
};

const STEP_PATHS: Record<number, string[]> = {
  0: ["planId", "billingCycle"],
  1: ["propertyName", "unitCount"],
  2: [
    "propertyInfo.legalName",
    "propertyInfo.phone",
    "propertyInfo.email",
    "propertyInfo.inDevelopment",
    "propertyInfo.address1",
    "propertyInfo.city",
    "propertyInfo.state",
    "propertyInfo.zip",
  ],
  3: ["signer.title", "signer.signerUserId", "signer.selfSigning"],
  4: ["payment.method", "payment.accountingEmail"],
};

const getFirstIncompleteStep = (values: NewPropertyFormValues): number => {
  try {
    validationSchema.validateSync(values, { abortEarly: false });
    return NEW_PROPERTY_STEPS.length;
  } catch (err) {
    const pathsWithErrors = new Set<string>();
    if (err instanceof yup.ValidationError) {
      if (err.inner?.length) {
        err.inner.forEach((e) => {
          if (e.path) pathsWithErrors.add(e.path);
        });
      } else if (err.path) {
        pathsWithErrors.add(err.path);
      }
    }
    for (let step = 0; step < NEW_PROPERTY_STEPS.length; step += 1) {
      const stepPaths = STEP_PATHS[step] ?? [];
      if (stepPaths.some((p) => pathsWithErrors.has(p))) return step;
    }
    return NEW_PROPERTY_STEPS.length;
  }
};

export type TeamUser = Omit<TeamUserGql, "__typename" | "directPhone">;

type StepsContentProps = CommonStepProps & {
  stepIndex: number;
  contractSuccess: ContractSuccessData | null;
}

const StepsContent: React.FC<StepsContentProps> = (props) => {
  // Pass a single superset of props to all steps; components will ignore extras.
  const StepComponents = [
    StepChoosePlan,
    StepAddNewProperty,
    StepPropertyInfo,
    StepSignerInfo,
    StepPayment,
    StepReview,
    StepSuccess,
  ] as const;
  const { stepIndex, ...restProps } = props;
  if (stepIndex === SUCCESS_STEP_INDEX) {
    const { contractSuccess } = restProps as StepsContentProps & {
      contractSuccess: ContractSuccessData | null;
    };
    if (!contractSuccess) return null;
    return (
      <StepSuccess
        key={contractSuccess.contractId ?? contractSuccess.variant}
        data={contractSuccess}
      />
    );
  }
  const Current = StepComponents[stepIndex];
  if (!Current) return null;
  const Comp = Current as unknown as React.ComponentType<Partial<CommonStepProps>>;
  return <Comp {...restProps} />;
};

const useStepUrlSync = (
  stepIndex: number,
  setStepIndex: (i: number) => void,
): void => {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const sub = location.pathname.split("/property/new")[1] || "/choose";
    const idx = NEW_PROPERTY_PATH_SUFFIX_TO_STEP[sub] ?? 0;
    if (idx !== stepIndex) setStepIndex(idx);
  }, [location.pathname]);

  React.useEffect(() => {
    const routes = [
      "/choose",
      "/add",
      "/info",
      "/signer",
      "/payment",
      "/review",
      "/success",
    ];
    navigate(`/dash/manager/settings/property/new${routes[stepIndex]}`, {
      replace: true,
    });
  }, [stepIndex, navigate]);
};

export type StepDirection = "initial" | "forward" | "backward" | "same";

export type PropertyContractStepEvent = {
  stepIndex: number;
  stepName: string;
  direction: StepDirection;
  previousStepName: string | null;
  propertyName: string | null;
}

export const buildPropertyContractStepEvent = (
  stepIndex: number,
  prevStepIndex: number | null,
  propertyName?: string | null,
): PropertyContractStepEvent => {
  let direction: StepDirection = "initial";
  if (prevStepIndex !== null) {
    if (stepIndex > prevStepIndex) direction = "forward";
    else if (stepIndex < prevStepIndex) direction = "backward";
    else direction = "same";
  }
  return {
    stepIndex,
    stepName: NEW_PROPERTY_STEPS[stepIndex] ?? "Unknown",
    direction,
    previousStepName:
      prevStepIndex === null
        ? null
        : NEW_PROPERTY_STEPS[prevStepIndex] ?? "Unknown",
    propertyName: propertyName || null,
  };
};

const parseGqlDecimal = (value: unknown): number | null => {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const NewProperty: React.FC = () => {
  useStepBreadcrumbsStyles();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = React.useState(getNewPropertyStepFromLocation);
  const [signatureRequestId, setSignatureRequestId] = React.useState<
  string | null
  >(null);
  const [contractSuccess, setContractSuccess] =
    React.useState<ContractSuccessData | null>(() => {
      if (getNewPropertyStepFromLocation() !== SUCCESS_STEP_INDEX) return null;
      return readStoredContractSuccess();
    });
  const viewer = usePostgresViewer();
  const viewerId = viewer?.id || "";
  const orgId = viewer?.organizationId || "";
  const [submissionId, setSubmissionId] = React.useState<string | null>(null);
  const { data: teamData } = useQuery<
  getTeamByOrgId,
  getTeamByOrgIdVariables
  >(TEAM_BY_ORG, {
    variables: { id: orgId },
    skip: !orgId,
    fetchPolicy: "cache-and-network",
  });
  const preloadedTeam = (teamData?.organization?.team || []).map((user) => ({
    id: user.id,
    fullName: user.fullName || "",
    email: user.email || "",
    roles: user.roles || [],
    title: user.title || null,
    isArchived: Boolean(user.isArchived),
  }));
  // Prefetch plans for Choose Plan step
  const {
    data: plansData,
    loading: plansLoading,
    error: plansErrorObj,
  } = useQuery<plansAll>(GET_PLANS_ALL, { fetchPolicy: "no-cache" });
  const { data: letterCostsData } = useQuery<invoicedLetterCosts>(
    GET_INVOICED_LETTER_COSTS,
    { fetchPolicy: "cache-first" },
  );
  const plans = React.useMemo(() => {
    const formatCurrency = (value: number): string =>
      `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

    const list = (plansData?.plansAll || []).filter((p) => p?.isPublic);
    return list.map((p) => {
      const dp: PlanPricingGql | null = p.defaultPricing;
      const pricingName = (dp?.pricingType?.name || "").toLowerCase();
      const isFlat = pricingName.includes("flat");
      const isPerUnit = pricingName.includes("unit");

      const minPrice = Number(dp?.minimumPrice ?? 0);
      const mppuRaw = dp?.monthlyPricePerUnit as unknown;
      const mppu =
        mppuRaw !== null && typeof mppuRaw !== "undefined" && Number.isFinite(Number(mppuRaw))
          ? Number(mppuRaw)
          : null;
      const threshold =
        dp?.unitThreshold !== null && typeof dp?.unitThreshold !== "undefined" &&
        Number.isFinite(Number(dp.unitThreshold))
          ? Number(dp.unitThreshold)
          : null;
      const annualDiscount = Number(dp?.annualDiscountPercent ?? 0);

      /** Monthly list price for a billing cycle (annual row applies % discount like the server). */
      const monthlyDisplayForCycle = (
        baseMonthly: number,
        cycle: "annually" | "quarterly",
      ): number => {
        let amount = baseMonthly;
        if (cycle === "annually" && annualDiscount > 0) {
          const factor = 1 - (annualDiscount / 100);
          amount = Math.round(amount * factor * 100) / 100;
        }
        return amount;
      };

      // Per-unit cards use monthlyPricePerUnit only; minimumPrice is a floor, not $/unit.
      const prices: PlanRow["prices"] = (() => {
        if (isFlat) {
          return {
            annually: `${formatCurrency(monthlyDisplayForCycle(minPrice, "annually"))}/month`,
            quarterly: `${formatCurrency(monthlyDisplayForCycle(minPrice, "quarterly"))}/month`,
          };
        }
        if (mppu === null) {
          return {
            annually:
              `${formatCurrency(monthlyDisplayForCycle(minPrice, "annually"))}/month minimum`,
            quarterly:
              `${formatCurrency(monthlyDisplayForCycle(minPrice, "quarterly"))}/month minimum`,
          };
        }
        return {
          annually: `${formatCurrency(monthlyDisplayForCycle(mppu, "annually"))}/unit/month`,
          quarterly: `${formatCurrency(monthlyDisplayForCycle(mppu, "quarterly"))}/unit/month`,
        };
      })();

      const details: string[] = [];
      if (isFlat && threshold !== null) {
        details.push(`Up to ${threshold.toLocaleString("en-US")} units`);
      }

      const priceSubtext =
        isPerUnit && threshold !== null
          ? `${formatCurrency(minPrice)}/month minimum for up to ${threshold.toLocaleString(
            "en-US",
          )} units`
          : null;

      const row: PlanRow = {
        key: p.id,
        title: p.name,
        prices,
        description: p.description,
        details,
      };
      if (priceSubtext !== null) {
        row.priceSubtext = priceSubtext;
      }
      return row;
    });
  }, [plansData?.plansAll]);
  const plansError = plansErrorObj
    ? plansErrorObj.message || "Failed to load plans"
    : null;
  const formik = useFormik<NewPropertyFormValues>({
    initialValues: NEW_PROPERTY_INITIAL_VALUES,
    // eslint-disable-next-line @typescript-eslint/no-empty-function, no-empty-function
    onSubmit: () => {},
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
  });
  const unitsForQuote =
    typeof formik.values.unitCount === "number" &&
    !Number.isNaN(formik.values.unitCount)
      ? formik.values.unitCount
      : 0;

  const { data: planQuoteData } = useQuery<
  planPricingQuote,
  planPricingQuoteVariables
  >(GET_PLAN_PRICING_QUOTE, {
    variables: {
      planId: formik.values.planId,
      billingCycle: formik.values.billingCycle,
      unitCount: unitsForQuote,
    },
    skip: !formik.values.planId,
    fetchPolicy: "cache-and-network",
  });

  const monthlyCost =
    parseGqlDecimal(planQuoteData?.planPricingQuote?.monthlyRecurring) ?? 0;
  const annualCost =
    parseGqlDecimal(planQuoteData?.planPricingQuote?.annualEquivalentTotal) ?? 0;
  const monthlyBeforeDiscount =
    parseGqlDecimal(planQuoteData?.planPricingQuote?.monthlyBeforeDiscount) ?? 0;
  const annualPaymentDiscountAmount =
    parseGqlDecimal(planQuoteData?.planPricingQuote?.annualPaymentDiscountAmount) ?? 0;
  const setupFeeOneTime =
    parseGqlDecimal(planQuoteData?.planPricingQuote?.oneTimeSetupFee) ?? 0;
  useStepUrlSync(stepIndex, setStepIndex);

  // Track each step of the add-property / contract-signing flow in PostHog so we
  // can see where people drop off and whether they navigate back and forth.
  const posthog = usePostHog();
  const prevStepIndexRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    if (posthog) {
      if (viewerId) {
        // Send a pseudonymous hash instead of the raw email so we keep stable
        // person de-duplication without transmitting PII to PostHog.
        sha256Hex(viewer?.email).then(emailHash => {
          if (cancelled) return;
          posthog.identify(viewerId, {
            organization: orgId,
            ...(emailHash ? { emailHash } : {}),
          });
        });
      }
      const prev = prevStepIndexRef.current;
      posthog.capture(
        "property_contract_step",
        buildPropertyContractStepEvent(stepIndex, prev, formik.values.propertyName),
      );
      prevStepIndexRef.current = stepIndex;
    }
    return () => {
      cancelled = true;
    };
  }, [stepIndex, posthog, viewerId, orgId]);

  // Load draft progress for current user
  const { refetch: refetchContractProgress } = useQuery(LOAD_PROPERTY_CONTRACT_PROGRESS, {
    skip: !viewerId,
    fetchPolicy: "network-only",
    onCompleted: (data: {
      propertyContractProgress?: {
        submissionId?: string;
        stepIndex?: number;
        valuesJson?: string;
      } | null;
    }) => {
      const p = data?.propertyContractProgress;
      const storedSuccess =
        getNewPropertyStepFromLocation() === SUCCESS_STEP_INDEX
          ? readStoredContractSuccess()
          : null;
      if (storedSuccess) {
        setContractSuccess(storedSuccess);
        setStepIndex(SUCCESS_STEP_INDEX);
        formik.resetForm({ values: NEW_PROPERTY_INITIAL_VALUES });
        return;
      }

      if (!p) {
        const urlStep = getNewPropertyStepFromLocation();
        const firstIncomplete = getFirstIncompleteStep(formik.values);
        setStepIndex(Math.min(urlStep, firstIncomplete, REVIEW_STEP_INDEX));
        return;
      }

      let loadedValues: NewPropertyFormValues | null = null;
      if (typeof p.valuesJson === "string" && p.valuesJson.trim() !== "") {
        try {
          const values = JSON.parse(p.valuesJson) as NewPropertyFormValues;
          loadedValues = values;
          formik.setValues(values, false);
        } catch {
          // ignore malformed json
        }
      }
      const savedStep = typeof p.stepIndex === "number" && p.stepIndex >= 0 && p.stepIndex < NEW_PROPERTY_STEPS.length
        ? p.stepIndex
        : 0;
      const valuesToCheck = loadedValues ?? formik.values;
      const firstIncomplete = getFirstIncompleteStep(valuesToCheck);
      const step = Math.min(savedStep, firstIncomplete, REVIEW_STEP_INDEX);
      setStepIndex(step);

      if (typeof p.submissionId === "string" && p.submissionId) {
        setSubmissionId(p.submissionId);
      }
    },
  });

  const [saveProgress] = useMutation(SAVE_PROPERTY_CONTRACT_PROGRESS);

  // Immediate persist helper for step transitions (avoids debounce cancellation on quick nav)
  const persistProgress = React.useCallback(
    async (overrideStepIndex?: number): Promise<void> => {
      if (!viewerId || signatureRequestId) return;
      const valuesJson = JSON.stringify(formik.values);
      try {
        const baseData = {
          stepIndex:
            typeof overrideStepIndex === "number"
              ? overrideStepIndex
              : stepIndex,
          valuesJson,
        };
        const data =
          submissionId && submissionId.trim() !== ""
            ? { ...baseData, submissionId }
            : baseData;
        const res = await saveProgress({
          variables: {
            data,
          },
        });
        const sid =
          res?.data?.savePropertyContractProgress?.submissionId || null;
        if (sid && sid !== submissionId) setSubmissionId(sid);
      } catch {
        // best-effort
      }
    },
    [
      viewerId,
      signatureRequestId,
      formik.values,
      stepIndex,
      submissionId,
      saveProgress,
      formik.values.propertyName,
    ],
  );

  // Persist draft progress via GQL until contract is sent
  React.useEffect(() => {
    let handle: ReturnType<typeof setTimeout> | null = null;
    if (viewerId && !signatureRequestId && stepIndex !== SUCCESS_STEP_INDEX) {
      handle = setTimeout(() => {
        const valuesJson = JSON.stringify(formik.values);
        const baseData = {
          stepIndex,
          valuesJson,
        };
        const data =
          submissionId
            ? { ...baseData, submissionId }
            : baseData;
        saveProgress({
          variables: { data },
        })
          .then((res) => {
            const sid = res?.data?.savePropertyContractProgress?.submissionId || null;
            if (sid && sid !== submissionId) setSubmissionId(sid);
          })
          .catch(() => {
            // best-effort
          });
      }, 400);
    }
    return () => {
      if (handle) clearTimeout(handle);
    };
  }, [
    viewerId,
    signatureRequestId,
    stepIndex,
    formik.values,
    submissionId,
    saveProgress,
    formik.values.propertyName,
  ]);

  const restoreFormFromServerProgress = React.useCallback(async (): Promise<void> => {
    try {
      const { data } = await refetchContractProgress();
      const p = data?.propertyContractProgress;
      if (typeof p?.valuesJson === "string" && p.valuesJson.trim() !== "") {
        try {
          const values = JSON.parse(p.valuesJson) as NewPropertyFormValues;
          formik.setValues(values, false);
        } catch {
          // ignore malformed json
        }
      }
    } catch {
      // best-effort
    }
  }, [refetchContractProgress, formik]);

  const leaveSuccessForStep = React.useCallback(
    async (index: number): Promise<void> => {
      clearStoredContractSuccess();
      setContractSuccess(null);
      await restoreFormFromServerProgress();
      persistProgress(index);
      setStepIndex(index);
    },
    [restoreFormFromServerProgress, persistProgress],
  );

  const handleBreadcrumbClick = (index: number) => {
    if (index < stepIndex) {
      if (stepIndex === SUCCESS_STEP_INDEX) {
        leaveSuccessForStep(index).catch(() => {});
        return;
      }
      persistProgress(index);
      setStepIndex(index);
    }
  };
  const letterCosts = letterCostsData?.invoicedLetterCosts ?? null;

  const getAtPath = (obj: unknown, path: string): unknown => {
    const parts = path.split(".");
    let current: unknown = obj as unknown;
    for (const part of parts) {
      if (current === null || typeof current !== "object") {
        current = null;
        break;
      }
      const asRecord = current as Record<string, unknown>;
      current = asRecord[part];
    }
    return current;
  };

  const buildTouchedFromPaths = (
    paths: string[],
  ): FormikTouched<NewPropertyFormValues> => {
    const root: Record<string, unknown> = {};
    const setDeep = (target: Record<string, unknown>, path: string[]): void => {
      const [head, ...rest] = path;
      if (!head) return;
      if (rest.length === 0) {
        target[head] = true;
        return;
      }
      const next = (target[head] ?? {}) as Record<string, unknown>;
      target[head] = next;
      setDeep(next, rest);
    };
    paths.forEach((p) => setDeep(root, p.split(".")));
    return root as FormikTouched<NewPropertyFormValues>;
  };

  const getRelevantPathsForStep = (idx: number): string[] =>
    STEP_PATHS[idx] ?? [];

  const onGoToSuccess = React.useCallback((data: ContractSuccessData) => {
    writeStoredContractSuccess(data);
    setContractSuccess(data);
    formik.resetForm({ values: NEW_PROPERTY_INITIAL_VALUES });
    setStepIndex(SUCCESS_STEP_INDEX);
  }, [formik]);

  React.useEffect(() => {
    if (stepIndex === SUCCESS_STEP_INDEX && !contractSuccess) {
      setStepIndex(REVIEW_STEP_INDEX);
    }
  }, [stepIndex, contractSuccess]);

  // Reset touched whenever step changes (fresh start per step)
  React.useEffect(() => {
    formik.setTouched({}, false);
  }, [stepIndex]);

  const goNext = (): void => {
    const next = stepIndex + 1;
    (async () => {
      // Leaving Step 2 (Add Property): set default legal name if empty
      if (stepIndex === 1) {
        const propName = (formik.values.propertyName || "").trim();
        const currentLegal = (
          formik.values.propertyInfo?.legalName || ""
        ).trim();
        if (propName !== "" && currentLegal === "") {
          await formik.setFieldValue("propertyInfo.legalName", propName, false);
        }
      }

      const relevantPaths = getRelevantPathsForStep(stepIndex);
      const touchedSubset = buildTouchedFromPaths(relevantPaths);
      formik.setTouched(touchedSubset, true);

      const errors = await formik.validateForm();
      const hasRelevantErrors = relevantPaths.some((p) => Boolean(getAtPath(errors, p)));
      if (hasRelevantErrors) return;
      if (next < NEW_PROPERTY_STEPS.length) {
        // Persist immediately when navigating forward
        persistProgress(next);
        setStepIndex(next);
      } else {
        navigate("/dash/manager/settings/contracts");
      }
    })();
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <StepBreadcrumbs
          steps={NEW_PROPERTY_STEPS}
          currentStepIndex={stepIndex}
          onClick={handleBreadcrumbClick}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormikProvider value={formik}>
          <StepLayout title={NEW_PROPERTY_STEPS[stepIndex]}>
            <StepsContent
              stepIndex={stepIndex}
              onNext={goNext}
              onGoToStep={(index) => {
                persistProgress(index);
                setStepIndex(index);
              }}
              onGoToSuccess={onGoToSuccess}
              contractSuccess={contractSuccess}
              monthlyCost={monthlyCost}
              annualCost={annualCost}
              monthlyBeforeDiscount={monthlyBeforeDiscount}
              annualPaymentDiscountAmount={annualPaymentDiscountAmount}
              setupFeeOneTime={setupFeeOneTime}
              letterCosts={letterCosts}
              formik={formik}
              preloadedTeam={preloadedTeam}
              orgId={orgId}
              plans={plans}
              plansLoading={plansLoading}
              plansError={plansError}
              signatureRequestId={signatureRequestId}
              setSignatureRequestId={setSignatureRequestId}
              submissionId={submissionId}
              setSubmissionId={setSubmissionId}
            />
          </StepLayout>
        </FormikProvider>
      </Grid>
    </Grid>
  );
};

export default NewProperty;
